import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { getMinimalCartEntryType } from 'features/cart/utils';
import {
  ModifiersSelections,
  SlotsSelections,
} from 'features/menu/components/product-wizard/types';

import { ILegacyCartEntry, ISanityImage } from '@rbi-ctg/menu';
import {
  EntryTypeEnum,
  IConfigOfferEntryDto,
  IMenuEntryDto,
  IMenuSelectionInput,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
} from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import {
  getEntryPriceOrZero,
  isCombo,
  isItem,
  isModifier,
  mapImageResourceToSanityImage,
} from 'state/unified-menu/utils';
import { isDummyItem } from 'utils/cart/is-dummy-item';
import { CartEntryType } from 'utils/cart/types';

export const getCartEntryId = ({ _id, sanityId }: ILegacyCartEntry): string =>
  sanityId?.trim().length ? sanityId : _id;

export function buildMinimalCartEntriesLookup(
  minimalCartEntries: ICartEntry[]
): Map<string, ICartEntry> {
  const minimalCartEntriesLookup = new Map<string, ICartEntry>();

  function addEntriesToLookup(entries: ICartEntry[]) {
    entries.forEach(entry => {
      minimalCartEntriesLookup.set(entry.id, entry);
      if (entry.children && entry.children.length > 0) {
        addEntriesToLookup(entry.children);
      }
    });
  }

  addEntriesToLookup(minimalCartEntries);

  return minimalCartEntriesLookup;
}

export function findModifierPrice(
  modifier: ILocalizedMenuEntry,
  parentMenuEntry: IMenuEntryDto,
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    posServiceMode: 'delivery' | 'pickup';
  }
): number {
  const modifierRef = parentMenuEntry.options?.entries?.find(entry =>
    modifier.id.includes(entry.entryId)
  );

  const modifierRefPrice = modifierRef?.price?.[options.posServiceMode] ?? 0;

  if (modifierRefPrice > 0) {
    return modifierRefPrice;
  }

  const modifierGroupIds = parentMenuEntry.options?.entries?.map(entry => entry.entryId) ?? [];
  for (const modifierGroupId of modifierGroupIds) {
    const modifierGroup = options.menu.get(modifierGroupId);
    const modifierGroupEntry = modifierGroup.options?.entries?.find(
      entry => entry.entryId === modifier.id
    );

    if (modifierGroupEntry) {
      const modifierGroupPrice = modifierGroupEntry.price?.[options.posServiceMode] ?? 0;
      return modifierGroupPrice;
    }
  }

  const modifierPrice = modifier.price?.[options.posServiceMode] ?? 0;
  return modifierPrice;
}

export function findComboSlotPrice(
  comboSlotMenuEntry: IMenuEntryDto,
  parentMenuEntry: IMenuEntryDto,
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    posServiceMode: 'delivery' | 'pickup';
  }
): number {
  const comboSlotsIds = parentMenuEntry.options?.entries?.map(entry => entry.entryId) ?? [];

  for (const comboSlotId of comboSlotsIds) {
    const comboSlot = options.menu.get(comboSlotId);
    const comboSlotEntry = comboSlot.options?.entries?.find(
      entry => entry.entryId === comboSlotMenuEntry.id
    );

    if (comboSlotEntry) {
      const comboSlotPrice = comboSlotEntry.price?.[options.posServiceMode] ?? 0;
      return comboSlotPrice;
    }
  }

  return 0;
}

// #region Mapping utils
export function mapLegacyToMinimalCartEntries(legacyCartEntries: ILegacyCartEntry[]): ICartEntry[] {
  return legacyCartEntries.map(legacyCartEntry => {
    const processedChildren = mapLegacyToMinimalCartEntries(legacyCartEntry.children);
    const processedMenuSelections = mapLegacyToMenuSelections(legacyCartEntry.children);

    return {
      cartId: legacyCartEntry.cartId,
      lineId: legacyCartEntry.cartId,
      id: getCartEntryId(legacyCartEntry),
      quantity: legacyCartEntry.quantity,
      name: legacyCartEntry.name,
      image: legacyCartEntry.image! as ISanityImage,
      url: legacyCartEntry.url,
      menuSelections: processedMenuSelections,
      type: MinimalCartEntryType.item,
      children: processedChildren,
      isExtra: legacyCartEntry.isExtra,
      isDonation: legacyCartEntry.isDonation,
    };
  });
}

export function mapLegacyToMenuSelections(
  legacyCartEntries: ILegacyCartEntry[]
): IMenuSelectionInput[] {
  return legacyCartEntries.flatMap(legacyCartEntry => {
    if (
      legacyCartEntry.type === CartEntryType.itemOption ||
      legacyCartEntry.type === CartEntryType.comboSlot ||
      legacyCartEntry.isDummyItem ||
      isDummyItem(legacyCartEntry)
    ) {
      return mapLegacyToMenuSelections(legacyCartEntry.children);
    }

    const nestedMenuSelections = mapLegacyToMenuSelections(legacyCartEntry.children);

    return [
      {
        id: getCartEntryId(legacyCartEntry),
        quantity: legacyCartEntry.quantity,
        menuSelections: nestedMenuSelections,
      },
    ];
  });
}

export function mapSelectedProductToMenuSelections(
  selectedProduct: ILocalizedMenuEntry,
  quantity: number,
  slotsSelections: SlotsSelections
): IMenuSelectionInput[] {
  return [
    {
      id: selectedProduct.id,
      quantity,
      menuSelections: mapSlotsSelectionsToMenuSelections(slotsSelections, isItem(selectedProduct)),
    },
  ];
}

export function mapSlotsSelectionsToMenuSelections(
  slotsSelections: SlotsSelections,
  isItem: boolean
): IMenuSelectionInput[] {
  return Object.values(slotsSelections)
    .filter(slotSelection => !!slotSelection)
    .flatMap(slotSelection => {
      const modifiersSelections = mapModifiersToMenuSelections(slotSelection!.modifiers ?? {});
      return isItem
        ? modifiersSelections
        : [
            {
              id: slotSelection!.itemId,
              quantity: 1,
              menuSelections: modifiersSelections,
            },
          ];
    });
}

export function mapModifiersToMenuSelections(
  modifiersSelections: ModifiersSelections
): IMenuSelectionInput[] {
  return Object.values(modifiersSelections).flatMap(multipliersSelections =>
    Object.entries(multipliersSelections).map(([multiplierKey, quantity]) => ({
      id: multiplierKey,
      quantity,
    }))
  );
}

export function mapChildrenPriceToMenuSelectionsPrice(
  cartEntry: ICartEntry
): IMenuSelectionInput[] {
  if (!cartEntry.menuSelections || !cartEntry.children) {
    return [...(cartEntry.menuSelections || [])];
  }

  // Create a map of ICartEntry
  const cartEntriesMap = buildMinimalCartEntriesLookup(cartEntry.children);

  // Recursively map menuSelections and add prices from children
  function mapMenuSelectionsWithPrices(
    menuSelections: readonly IMenuSelectionInput[]
  ): IMenuSelectionInput[] {
    return menuSelections.map(menuSelection => {
      const childCartEntry = cartEntriesMap.get(menuSelection.id);

      const updatedMenuSelection: IMenuSelectionInput = {
        ...menuSelection,
        ...(!!childCartEntry?.price && {
          price: childCartEntry.price,
        }),
        // Recursively process nested menuSelections
        ...(menuSelection.menuSelections &&
          menuSelection.menuSelections.length > 0 && {
            menuSelections: mapMenuSelectionsWithPrices(menuSelection.menuSelections),
          }),
      };

      return updatedMenuSelection;
    });
  }

  return mapMenuSelectionsWithPrices(cartEntry.menuSelections);
}

// Offer combos can arrive flattened as `offer -> items`; normalize them to
// `offer -> combo -> items` so they match reward combo structure while keeping
// the offer id as the parent wrapper and the combo benefit id as the child.
export function normalizeIncentiveComboEntry<T extends ICartEntry>(
  minimalCartEntry: T,
  isComboBenefit: boolean,
  loyaltyWrapperId?: string,
  comboBenefitId?: string
): T {
  if (minimalCartEntry.type !== MinimalCartEntryType.incentive || !isComboBenefit) {
    return minimalCartEntry;
  }

  if (minimalCartEntry.children?.[0]?.type === MinimalCartEntryType.combo) {
    return minimalCartEntry;
  }

  const zeroPrice = {
    amount: 0,
    currency: 'USD',
  } as const;

  const wrapperId = loyaltyWrapperId ?? minimalCartEntry.id;
  const benefitId = comboBenefitId ?? minimalCartEntry.id;
  const comboBenefitEntry: ICartEntry = {
    ...minimalCartEntry,
    id: benefitId,
    type: MinimalCartEntryType.combo,
    quantity: 1,
    price: zeroPrice,
    menuSelections: minimalCartEntry.menuSelections,
    children: minimalCartEntry.children,
  };

  return {
    ...minimalCartEntry,
    id: wrapperId,
    children: [comboBenefitEntry],
    menuSelections: [
      {
        id: comboBenefitEntry.id,
        quantity: 1,
        price: zeroPrice,
        menuSelections: comboBenefitEntry.menuSelections,
      },
    ],
  } as T;
}

export function mapMenuSelectionsToICartEntry(
  menuSelections: IMenuSelectionInput[],
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
    indexedRewards: IndexedMenu<IRewardEntryDto>;
    posServiceMode: 'delivery' | 'pickup';
    url: string;
    parentMenuEntry?: IMenuEntryDto;
    rewardId?: string;
  }
): ICartEntry[] {
  return menuSelections.map(
    (menuSelection): ICartEntry => {
      const menuEntry = options.menu.find(menuSelection.id);
      const fallbackChildren: ICartEntry[] = mapMenuSelectionsToICartEntry(
        (menuSelection.menuSelections as IMenuSelectionInput[]) ?? [],
        { ...options, parentMenuEntry: menuEntry }
      );

      if (!menuEntry) {
        const fallbackType = getMinimalCartEntryType(menuSelection.id, {
          indexedMenu: options.menu,
          indexedOffers: options.indexedOffers,
          indexedRewards: options.indexedRewards,
        });

        return {
          ...menuSelection,
          name: '',
          image: mapImageResourceToSanityImage('', ''),
          price: {
            amount: 0,
            currency: 'USD',
          },
          referenceId: options.indexedOffers.find(menuSelection.id)?.loyaltyEngineId,
          type: fallbackType,
          children: fallbackChildren,
          url: options.url,
        };
      }

      let price = menuEntry ? getEntryPriceOrZero(menuEntry, options.posServiceMode) : 0;

      if (menuEntry && options.parentMenuEntry && isCombo(options.parentMenuEntry)) {
        price = findComboSlotPrice(menuEntry, options.parentMenuEntry, options);
      }

      if (menuEntry && options.parentMenuEntry && isModifier(menuEntry)) {
        price = findModifierPrice(menuEntry, options.parentMenuEntry, options);
      }

      const type = getMinimalCartEntryType(menuEntry?.id ?? '', {
        indexedMenu: options.menu,
        indexedOffers: options.indexedOffers,
        indexedRewards: options.indexedRewards,
      });

      let referenceId: string | undefined = undefined;

      if (type === MinimalCartEntryType.incentive || type === MinimalCartEntryType.reward) {
        const offerEntry = options.indexedOffers.find(menuEntry?.id ?? '');
        referenceId = offerEntry?.loyaltyEngineId ?? undefined;
      }

      let url = options.url;
      if (type === MinimalCartEntryType.reward) {
        url = `${url}?rewardId=${options.rewardId}`;
      }

      return {
        ...menuSelection,
        name: menuEntry?.name?.locale ?? '',
        rewardIdReference: menuEntry?.rewardIdReference,
        image: mapImageResourceToSanityImage(
          menuEntry?.image?.resource ?? '',
          menuEntry?.image?.altText?.locale ?? ''
        ),
        price: {
          amount: price,
          currency: 'USD',
        },
        referenceId,
        type,
        children: fallbackChildren,
        url,
      };
    }
  );
}

/**
 *  @deprecated use mapMenuSelectionsToICartEntry instead
 *  this is still supporting the old structure of the minimal cart entry
 */
export function mapMenuSelectionsToMinimalCartEntry(
  menuSelections: IMenuSelectionInput[],
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
    indexedRewards: IndexedMenu<IRewardEntryDto>;
    posServiceMode: 'delivery' | 'pickup';
    url: string;
    rewardId?: string;
    offerId?: string;
  }
): ICartEntry[] {
  return menuSelections.map(menuSelection => {
    const menuEntry = options.menu.find(menuSelection.id);

    const isOfferEntry = !!options.offerId;
    const isRewardEntry = !!options.indexedRewards.find(menuSelection.id);
    const isLoyaltyEntry = isOfferEntry || isRewardEntry;

    let isOfferPicker = false;
    const offerItem = options.indexedOffers.find(options.offerId ?? '');
    if (offerItem) {
      const benefitItemId = offerItem.options?.[0]?.entryId;
      if (benefitItemId) {
        const benefitItem = options.menu.find(benefitItemId);
        if (benefitItem?.type === EntryTypeEnum.PICKER) {
          isOfferPicker = true;
        }
      }
    }

    const rewardEntry = options.indexedRewards.find(menuEntry?.id ?? '');

    const children = mapMenuSelectionsToMinimalCartEntry(
      (menuSelection.menuSelections as IMenuSelectionInput[]) ?? [],
      { ...options, offerId: undefined, rewardId: undefined }
    );

    const image = mapImageResourceToSanityImage(
      menuEntry?.image?.resource ?? '',
      menuEntry?.image?.altText?.locale ?? ''
    );

    const loyaltyId = isOfferEntry
      ? options.offerId
      : isRewardEntry
      ? rewardEntry?.options?.[0]?.entryId
      : menuEntry?.id;

    /**
     * for most cases including offerCombos menu selections have the same structure as the children
     */
    const output = {
      ...menuSelection,
      ...(isLoyaltyEntry && {
        id: loyaltyId ?? '',
      }),
      name: menuEntry?.name?.locale ?? '',
      image,
      type: MinimalCartEntryType.item,
      url: options.url,
      children,
      referenceId: menuEntry?.loyaltyEngineIdReference,
    };

    /**
     * note that for offer pickers the menu selections needs to be a little different eg:
      "menuSelections": [
        {
          "id": <id of the offer>,
          "quantity": 1,
          "menuSelections": [
            {
              "id": <id of the combo>,
              "quantity": 1,
              "menuSelections": [
                {
                  "id": <id of the item>,
                  "quantity": 1,
                  "menuSelections": []
                },
              ]
            }
          ]
        }
      ]
     */
    if (isOfferPicker) {
      const comboSelections = output.menuSelections;
      output.menuSelections = [
        {
          id: menuEntry?.id ?? '',
          quantity: 1,
          menuSelections: comboSelections,
        },
      ];

      // In order to be able to edit the offer from the cart we need to change the url to the
      // picker entry and add the offerId to the query params
      const pickerEntryUrl = output.url
        .replace('offer', 'picker')
        .replace(options.offerId ?? '', menuEntry?.id ?? '');
      output.url = `${pickerEntryUrl}?offerId=${options.offerId}`;
      return output;
    }

    return output;
  });
}

export { isDummyItem };
// #endregion

// #region Hydration utils
export {
  findMinimalCartEntryBySanityId,
  findRewardBenefitFromHydratedCartEntry,
  findMatchingCartEntry,
  isHydratedReward,
  getHydratedEntityId,
  mapHydratedCart,
  mapServiceModeToCartServiceMode,
} from 'features/cart/utils/hydration';
// #endregion
